import { OrderHistoryInstanceType } from '@flipster/apis/v2/history/instance';
import { ProductType } from '@flipster/apis/v2/history/types';
import { TransOrderType } from '@flipster/apis/v2/order/trans';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { TableCell, TableRow } from '@flipster/ui/Table';
import { DateFormatter } from '@flipster/utils/datetime';

import { SymbolDisplay } from '../../../misc/display';
import { useRawSnapshotSpec } from '../../../shared/snapshot/use-snapshot-spec';
import { ExecFeeDisplay } from '../exec-fee-display';
import { HistoryOrderSideBadge } from '../order-side-badge';

interface Props {
  item: OrderHistoryInstanceType;
  productType: ProductType;
}

export const OrderDesktopRow = ({ item, productType }: Props) => {
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();

  const instrument = instrumentsKeyBySymbol?.[item.symbol];

  return (
    <TableRow>
      <TableCell>
        <Datetime value={item.orderTime} format={DateFormatter.DateTimeSec} css={{ textStyle: 'Legacy/Number3' }} />
      </TableCell>
      <TableCell>
        <SymbolDisplay
          symbol={item.symbol}
          icon={<SymbolDisplay.Icon size={24} />}
          mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
        />
      </TableCell>
      <TableCell>
        <HistoryOrderSideBadge orderSide={item.side} productType={productType} />
      </TableCell>
      <TableCell>{TransOrderType[item.orderType]}</TableCell>
      <TableCell align='right'>
        <div>
          <Formatted.Number
            value={item.avgPrice}
            zeroString='-'
            precision={instrument?.pricePrecision}
            toFixed
            css={{ textStyle: 'Text/M/Monospace/Regular' }}
            suffix={instrument?.quoteCurrency}
          />
          <br />
          {item.isMarket ? (
            <span className={css({ color: 'content.default.level-2' })}>Market</span>
          ) : (
            <Formatted.Number
              value={item.price}
              zeroString='-'
              precision={instrument?.pricePrecision}
              toFixed
              css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' }}
              suffix={instrument?.quoteCurrency}
            />
          )}
        </div>
      </TableCell>
      <TableCell align='right'>
        <div>
          <Formatted.Number
            value={item.cumQty}
            zeroString='-'
            precision={instrument?.tradeQtyPrecision}
            toFixed
            css={{ textStyle: 'Text/M/Monospace/Regular' }}
          />
          <br />
          <Formatted.Number
            value={item.orderQty}
            zeroString='-'
            precision={instrument?.tradeQtyPrecision}
            toFixed
            css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' }}
          />
        </div>
      </TableCell>
      <TableCell align='right'>
        <ExecFeeDisplay
          originalExecFee={item.cumFee}
          aqxpBonusRedemption={item.cumFeeAqxpRedeemed}
          voucherBonusRedemption={item.cumFeeVoucherRedeemed}
          suffix={instrument?.quoteCurrency}
        />
      </TableCell>
      <TableCell align='right'>
        <Formatted.Number
          value={item.fillValue}
          precision={2}
          zeroString='-'
          css={{ textStyle: 'Text/M/Monospace/Regular' }}
          suffix={instrument?.quoteCurrency}
        />
      </TableCell>
      <TableCell align='right'>
        <div>
          <p>{item.transOrderStatus}</p>
          {!item.isOpenOrder && (
            <Datetime value={item.transactTime} css={{ color: 'legacy.gray.8', textStyle: 'Legacy/Number4' }} />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
